import { useState, type KeyboardEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import type { WaveSource } from "@/components/admin_dashboard/FollowUpWavesEditor";
import {
  HeadingWithTip,
  InfoTip,
  MessageContentFields,
  NumberField,
  useServerWorkflowSettings,
  useStoredSettings,
  type MessageContentValue,
  type WorkflowMessageSettingsRow,
} from "@/components/admin_dashboard/WorkflowSettingsFields";
import { readEdgeError } from "@/lib/edgeError";
import {
  fetchInstagramCommentsConnection,
  reconnectInstagramForComments,
} from "@/lib/instagramCommentDm";
import { instagramOAuthConfigured } from "@/lib/instagramOAuth";

const MAX_KEYWORDS = 10;
const MAX_KEYWORD_LENGTH = 40;
// Instagram's message limit is 1000; the connector keeps a margin at 950.
const MAX_DM_LENGTH = 950;
const MAX_COMMENT_REPLY_LENGTH = 300;
const MIN_FEEDBACK_DELAY_HOURS = 1;
const MAX_FEEDBACK_DELAY_HOURS = 168;

export interface InstagramSettings {
  keywords: string[];
  matchMode: "contains" | "exact";
  dmEnabled: boolean;
  dmMessage: string;
  commentReplyEnabled: boolean;
  commentReply: string;
}

const DEFAULT_INSTAGRAM_SETTINGS: InstagramSettings = {
  keywords: [],
  matchMode: "contains",
  dmEnabled: true,
  dmMessage: "",
  commentReplyEnabled: false,
  commentReply: "",
};

export function instagramSettingsFromServer(
  row: WorkflowMessageSettingsRow,
  initial: InstagramSettings = DEFAULT_INSTAGRAM_SETTINGS,
): InstagramSettings {
  const cfg = (row.config && typeof row.config === "object" && !Array.isArray(row.config))
    ? row.config
    : {};
  const keywords = Array.isArray(cfg.keywords)
    ? cfg.keywords
      .filter((k): k is string => typeof k === "string" && k.trim().length > 0)
      .map((k) => k.trim().slice(0, MAX_KEYWORD_LENGTH))
      .slice(0, MAX_KEYWORDS)
    : initial.keywords;
  return {
    keywords,
    matchMode: cfg.match_mode === "exact" ? "exact" : "contains",
    dmEnabled: typeof cfg.dm_enabled === "boolean" ? cfg.dm_enabled : initial.dmEnabled,
    dmMessage: typeof cfg.dm_message === "string" ? cfg.dm_message.slice(0, MAX_DM_LENGTH) : initial.dmMessage,
    commentReplyEnabled: typeof cfg.comment_reply_enabled === "boolean"
      ? cfg.comment_reply_enabled
      : initial.commentReplyEnabled,
    commentReply: typeof cfg.comment_reply === "string"
      ? cfg.comment_reply.slice(0, MAX_COMMENT_REPLY_LENGTH)
      : initial.commentReply,
  };
}

export function instagramSettingsToServer(settings: InstagramSettings): WorkflowMessageSettingsRow {
  return {
    content_mode: "template",
    config: {
      keywords: settings.keywords.slice(0, MAX_KEYWORDS),
      match_mode: settings.matchMode,
      dm_enabled: settings.dmEnabled,
      dm_message: settings.dmMessage.slice(0, MAX_DM_LENGTH),
      comment_reply_enabled: settings.commentReplyEnabled,
      comment_reply: settings.commentReply.slice(0, MAX_COMMENT_REPLY_LENGTH),
    },
  };
}

/** Shown until comments can reach Dibs: Instagram isn't connected, or was
 *  connected before Dibs asked for permission to read comments. */
function InstagramCommentsConnectionNotice({ businessId }: { businessId?: string }) {
  const queryClient = useQueryClient();
  const [connecting, setConnecting] = useState(false);
  const queryKey = ["instagram-comments-connection", businessId];
  const { data: connection } = useQuery({
    queryKey,
    enabled: !!businessId,
    queryFn: () => fetchInstagramCommentsConnection(businessId as string),
  });
  if (!connection || connection === "ready") return null;

  const reconnect = connection === "needs_reconnect";
  const connect = async () => {
    setConnecting(true);
    try {
      await reconnectInstagramForComments();
      await queryClient.invalidateQueries({ queryKey });
      toast.success("Instagram connected. Comments can now get a DM.");
    } catch (err: any) {
      const msg = err?.message?.includes("cancelled") || err?.message?.includes("Popup")
        ? err.message
        : await readEdgeError(err, "Couldn't connect Instagram.");
      toast.error(msg);
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div role="status" className="rounded-md border border-border bg-muted/40 p-3">
      <p className="text-sm font-medium text-foreground">
        {reconnect ? "Reconnect Instagram to use this" : "Connect Instagram to use this"}
      </p>
      <p className="mt-1 text-xs leading-5 text-muted-foreground">
        {reconnect
          ? "Dibs needs permission to read the comments on your posts."
          : "This workflow replies to comments on your Instagram posts."}
      </p>
      {instagramOAuthConfigured() ? (
        <Button size="sm" variant="outline" className="mt-3 h-8 text-xs" disabled={connecting} onClick={connect}>
          {connecting && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
          {reconnect ? "Reconnect Instagram" : "Connect Instagram"}
        </Button>
      ) : (
        <p className="mt-1 text-xs leading-5 text-muted-foreground">You can do this in Settings, under Connections.</p>
      )}
    </div>
  );
}

export const InstagramCommentDmSettings = ({ businessId }: { businessId?: string }) => {
  const [settings, update] = useServerWorkflowSettings<InstagramSettings>({
    businessId,
    workflowId: "instagram-comment-dm",
    storageKey: `dibs:workflow-instagram-comment-dm:${businessId ?? "guest"}`,
    initial: DEFAULT_INSTAGRAM_SETTINGS,
    fromServer: instagramSettingsFromServer,
    toServer: instagramSettingsToServer,
  });
  const [keywordDraft, setKeywordDraft] = useState("");
  const atLimit = settings.keywords.length >= MAX_KEYWORDS;

  const addKeyword = () => {
    const keyword = keywordDraft.trim().slice(0, MAX_KEYWORD_LENGTH);
    if (!keyword || atLimit) return;
    const exists = settings.keywords.some((item) => item.toLocaleLowerCase() === keyword.toLocaleLowerCase());
    if (!exists) update({ keywords: [...settings.keywords, keyword] });
    setKeywordDraft("");
  };

  const onKeywordKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      addKeyword();
    } else if (event.key === "Backspace" && !keywordDraft && settings.keywords.length > 0) {
      update({ keywords: settings.keywords.slice(0, -1) });
    }
  };

  return (
    <div className="space-y-6">
      <InstagramCommentsConnectionNotice businessId={businessId} />
      <div>
        <HeadingWithTip
          id="ig-keywords-heading"
          title="Trigger keywords"
          tip="When someone comments one of these on any of your posts or videos, Dibs sends them a DM. Your Instagram account needs to be public. Press Enter to add each keyword."
        />
        <div className="mt-3">
          <Input
            value={keywordDraft}
            maxLength={MAX_KEYWORD_LENGTH}
            disabled={atLimit}
            placeholder={atLimit ? `Up to ${MAX_KEYWORDS} keywords` : "e.g. TRIAL"}
            aria-labelledby="ig-keywords-heading"
            className="h-9 text-xs"
            onChange={(event) => setKeywordDraft(event.target.value)}
            onKeyDown={onKeywordKeyDown}
            onBlur={addKeyword}
          />
        </div>
        {settings.keywords.length > 0 && (
          <ul className="mt-2 flex flex-wrap gap-1.5" aria-label="Keywords">
            {settings.keywords.map((keyword) => (
              <li
                key={keyword}
                className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/60 py-0.5 pl-2.5 pr-1 text-xs font-medium text-foreground"
              >
                {keyword}
                <button
                  type="button"
                  aria-label={`Remove ${keyword}`}
                  className="rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => update({ keywords: settings.keywords.filter((item) => item !== keyword) })}
                >
                  <X className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <p id="ig-match-label" className="text-xs font-medium text-foreground">Match</p>
        <RadioGroup
          className="mt-2 gap-2"
          value={settings.matchMode}
          aria-labelledby="ig-match-label"
          onValueChange={(value) => update({ matchMode: value as InstagramSettings["matchMode"] })}
        >
          {([
            ["contains", "Contains the keyword", "“Where do I sign up for the trial?” matches TRIAL."],
            ["exact", "Is exactly the keyword", "Only comments that are just the keyword match."],
          ] as const).map(([value, label, hint]) => (
            <label
              key={value}
              className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
                settings.matchMode === value ? "border-foreground/35 bg-muted/60" : "border-border hover:bg-muted/40"
              }`}
            >
              <RadioGroupItem value={value} aria-label={label} className="mt-0.5 border-foreground text-foreground" />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-foreground">{label}</span>
                <span className="mt-1 block text-xs leading-4 text-muted-foreground">{hint}</span>
              </span>
            </label>
          ))}
        </RadioGroup>
      </div>

      <div>
        <HeadingWithTip
          id="ig-actions-heading"
          title="Actions"
          tip="What Dibs does when a comment matches a keyword. Each person gets this once per post, and you can turn each action on or off."
        />

        <ul className="mt-4 space-y-5" aria-labelledby="ig-actions-heading">
          <li>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <label htmlFor="ig-dm-message" className="text-xs font-medium text-foreground">Send a DM</label>
                <InfoTip label="About sending a DM">
                  Sent from the connected account the comment was left on. Instagram allows one DM per comment; once they reply, the conversation continues in the inbox.
                </InfoTip>
              </div>
              <Switch
                checked={settings.dmEnabled}
                onCheckedChange={(dmEnabled) => update({ dmEnabled })}
                aria-label={`${settings.dmEnabled ? "Turn off" : "Turn on"} sending a DM`}
                className="origin-right scale-90"
              />
            </div>
            {settings.dmEnabled && (
              <>
                <Textarea
                  id="ig-dm-message"
                  value={settings.dmMessage}
                  maxLength={MAX_DM_LENGTH}
                  placeholder="e.g. Hi! Thanks for your comment. Here's the link to book your free trial: …"
                  className="mt-2 min-h-[120px] text-xs"
                  onChange={(event) => update({ dmMessage: event.target.value })}
                />
                <p className="mt-1.5 text-right text-xs text-muted-foreground">
                  {settings.dmMessage.length} / {MAX_DM_LENGTH}
                </p>
              </>
            )}
          </li>

          <li>
            <div className="flex items-center justify-between gap-3">
              <label htmlFor="ig-comment-reply" className="text-xs font-medium text-foreground">
                Reply to the comment
              </label>
              <Switch
                checked={settings.commentReplyEnabled}
                onCheckedChange={(commentReplyEnabled) => update({ commentReplyEnabled })}
                aria-label={`${settings.commentReplyEnabled ? "Turn off" : "Turn on"} replying to the comment`}
                className="origin-right scale-90"
              />
            </div>
            {settings.commentReplyEnabled && (
              <Textarea
                id="ig-comment-reply"
                value={settings.commentReply}
                maxLength={MAX_COMMENT_REPLY_LENGTH}
                placeholder="e.g. Just sent you a DM!"
                className="mt-2 min-h-[64px] text-xs"
                onChange={(event) => update({ commentReply: event.target.value })}
              />
            )}
          </li>
        </ul>
      </div>
    </div>
  );
};

interface FeedbackSettings extends MessageContentValue {
  delayHours: number;
}

interface FeedbackSettingsProps {
  businessId?: string;
  sources: WaveSource[];
  sourcesLoading: boolean;
}

export const PostSessionFeedbackSettings = ({ businessId, sources, sourcesLoading }: FeedbackSettingsProps) => {
  const [settings, update] = useStoredSettings<FeedbackSettings>(
    `dibs:workflow-post-session-feedback:${businessId ?? "guest"}`,
    { delayHours: 2, contentMode: "ai", instructions: "", sourceId: null },
  );

  return (
    <div className="space-y-6">
      <div>
        <HeadingWithTip
          id="feedback-timing-heading"
          title="Timing"
          tip="Dibs asks once, after a customer's first session has finished. It never asks a customer twice."
        />
        <div className="mt-3">
          <NumberField
            id="feedback-timing"
            labelledBy="feedback-timing-heading"
            value={settings.delayHours}
            min={MIN_FEEDBACK_DELAY_HOURS}
            max={MAX_FEEDBACK_DELAY_HOURS}
            suffix={`${settings.delayHours === 1 ? "hour" : "hours"} after the first session ends`}
            onCommit={(delayHours) => update({ delayHours })}
          />
        </div>
      </div>

      <MessageContentFields
        id="feedback"
        noun="request"
        tip="Choose how Dibs writes the feedback request."
        instructionsPlaceholder="e.g. Ask for a rating out of 5 and what we could improve."
        value={settings}
        sources={sources}
        sourcesLoading={sourcesLoading}
        onChange={update}
      />
    </div>
  );
};
